import { useState } from 'react';
import JSZip from 'jszip';
import {
  Box,
  Button,
  VStack,
  Text,
  useToast,
  Progress,
  Grid,
  AspectRatio,
  Image,
  Badge,
  Center,
  Spinner,
} from '@chakra-ui/react';
import { UploadSimple, ImageSquare } from '@phosphor-icons/react';
import DetectionBoxes from '../../media/DetectionBoxes';

export default function DatasetValidator() {
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [validationResults, setValidationResults] = useState([]);
  const toast = useToast();

  const processYoloDataset = async (files) => {
    const imageFiles = [];
    const labelFiles = [];
    let dataYaml = null;

    // First pass - categorize files
    for (const file of files) {
      if (file.name === 'data.yaml') {
        dataYaml = file;
      } else if (file.name.endsWith('.txt')) {
        labelFiles.push(file);
      } else if (file.name.match(/\.(jpg|jpeg|png)$/i)) {
        imageFiles.push(file);
      }
    }

    // Process YAML file
    if (!dataYaml) {
      throw new Error('data.yaml not found in the dataset');
    }

    const yamlContent = await dataYaml.text();
    const classNames = yamlContent.match(/names:\s*\[(.*?)\]/)[1]
      .split(',')
      .map(name => name.trim().replace(/['"]/g, ''));

    // Process image and label pairs
    const results = [];
    for (const imageFile of imageFiles) {
      const baseName = imageFile.name.substring(0, imageFile.name.lastIndexOf('.'));
      const labelFile = labelFiles.find(f => 
        f.name === `${baseName}.txt` ||
        f.name === `${baseName}.label`
      );

      if (!labelFile) {
        continue;
      }

      const imgUrl = URL.createObjectURL(imageFile);
      const labelContent = await labelFile.text();
      const boxes = labelContent.split('\n')
        .filter(line => line.trim())
        .map(line => {
          const [classId, x, y, width, height] = line.split(' ').map(Number);
          return {
            class: classNames[classId],
            x,
            y,
            width,
            height
          };
        });

      results.push({
        imageFile: imageFile.name,
        imageUrl: imgUrl,
        boxes
      });
    }

    return results;
  };

  const processCocoDataset = async (files) => {
    const imageFiles = [];
    let annotationsFile = null;

    // First pass - categorize files
    for (const file of files) {
      if (file.name === 'annotations.json' || file.name.endsWith('_annotations.json')) {
        annotationsFile = file;
      } else if (file.name.match(/\.(jpg|jpeg|png)$/i)) {
        imageFiles.push(file);
      }
    }

    if (!annotationsFile) {
      throw new Error('annotations.json not found in the dataset');
    }

    // Process annotations file
    const annotationsContent = await annotationsFile.text();
    const coco = JSON.parse(annotationsContent);
    
    const categoryMap = new Map(
      coco.categories.map(cat => [cat.id, cat.name])
    );

    // Process images and their annotations
    const results = [];
    for (const imageFile of imageFiles) {
      const cocoImage = coco.images.find(img => img.file_name === imageFile.name);
      if (!cocoImage) continue;

      const annotations = coco.annotations
        .filter(ann => ann.image_id === cocoImage.id)
        .map(ann => ({
          class: categoryMap.get(ann.category_id),
          x: (ann.bbox[0] + ann.bbox[2] / 2) / cocoImage.width,
          y: (ann.bbox[1] + ann.bbox[3] / 2) / cocoImage.height,
          width: ann.bbox[2] / cocoImage.width,
          height: ann.bbox[3] / cocoImage.height
        }));

      results.push({
        imageFile: imageFile.name,
        imageUrl: URL.createObjectURL(imageFile),
        boxes: annotations
      });
    }

    return results;
  };

  const handleFileDrop = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    setProgress(0);
    setValidationResults([]);

    try {
      const items = e.dataTransfer.items;
      const files = [];
      let format = null;

      // Check for zip file
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file.name.endsWith('.zip')) {
            const zip = new JSZip();
            const contents = await zip.loadAsync(file);
            
            // Extract all files
            const zipFiles = [];
            for (const [path, zipEntry] of Object.entries(contents.files)) {
              if (!zipEntry.dir) {
                const blob = await zipEntry.async('blob');
                zipFiles.push(new File([blob], path.split('/').pop()));
              }
            }

            // Determine format
            if (zipFiles.some(f => f.name === 'data.yaml')) {
              format = 'yolo';
            } else if (zipFiles.some(f => f.name === 'annotations.json')) {
              format = 'coco';
            }

            files.push(...zipFiles);
            break;
          }
        }
      }

      // If no zip file found, process dropped files directly
      if (files.length === 0) {
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item.kind === 'file') {
            files.push(item.getAsFile());
          }
        }

        // Determine format from loose files
        if (files.some(f => f.name === 'data.yaml')) {
          format = 'yolo';
        } else if (files.some(f => f.name === 'annotations.json')) {
          format = 'coco';
        }
      }

      if (!format) {
        throw new Error('Could not determine dataset format. Please include data.yaml for YOLO or annotations.json for COCO.');
      }

      setProgress(20);

      let results;
      if (format === 'yolo') {
        results = await processYoloDataset(files);
      } else {
        results = await processCocoDataset(files);
      }

      setProgress(100);
      setValidationResults(results);

      toast({
        title: 'Dataset Processed',
        description: `Successfully processed ${results.length} images with annotations`,
        status: 'success',
        duration: 5000,
      });
    } catch (error) {
      console.error('Error processing dataset:', error);
      toast({
        title: 'Error Processing Dataset',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  return (
    <Box>
      <VStack spacing={6} align="stretch">
        <Box
          border="2px dashed"
          borderColor="gray.300"
          borderRadius="md"
          p={8}
          textAlign="center"
          onDrop={handleFileDrop}
          onDragOver={handleDragOver}
        >
          <VStack spacing={4}>
            <UploadSimple size={48} />
            <Text>
              Drop your YOLO or COCO dataset here (zip file or individual files)
            </Text>
            <Text fontSize="sm" color="gray.500">
              Include data.yaml + images + labels for YOLO format, or annotations.json + images for COCO format
            </Text>
          </VStack>
        </Box>

        {isProcessing && (
          <Progress
            value={progress}
            size="sm"
            colorScheme="blue"
            hasStripe
            isAnimated
          />
        )}

        {validationResults.length > 0 && (
          <Grid templateColumns="repeat(auto-fill, minmax(200px, 1fr))" gap={6}>
            {validationResults.map((result, index) => (
              <Box
                key={index}
                borderWidth="1px"
                borderRadius="lg"
                overflow="hidden"
                position="relative"
              >
                <AspectRatio ratio={16/9}>
                  <>
                    <Image
                      src={result.imageUrl}
                      alt={result.imageFile}
                      objectFit="cover"
                      fallback={
                        <Center bg="gray.100">
                          <ImageSquare size={32} />
                        </Center>
                      }
                    />
                    <Box position="absolute" inset="0">
                      {result.boxes.map((box, idx) => (
                        <Box
                          key={idx}
                          position="absolute"
                          top={`${box.y * 100}%`}
                          left={`${box.x * 100}%`}
                          width={`${box.width * 100}%`}
                          height={`${box.height * 100}%`}
                          transform="translate(-50%, -50%)"
                          border="2px solid"
                          borderColor="blue.500"
                          backgroundColor="transparent"
                          pointerEvents="none"
                          zIndex={2}
                        >
                          <Box
                            position="absolute"
                            top="-24px"
                            left="50%"
                            transform="translateX(-50%)"
                            backgroundColor="blue.500"
                            color="white"
                            px="2"
                            py="1"
                            borderRadius="md"
                            fontSize="xs"
                            fontWeight="bold"
                            zIndex={3}
                            whiteSpace="nowrap"
                          >
                            {box.class}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </>
                </AspectRatio>
                <Box p={2}>
                  <Text fontSize="sm" noOfLines={1}>{result.imageFile}</Text>
                  <Badge colorScheme="green">{result.boxes.length} annotations</Badge>
                </Box>
              </Box>
            ))}
          </Grid>
        )}
      </VStack>
    </Box>
  );
}
